import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, MapPin, DollarSign, Phone, Mail, Image, X, Filter, Search, Pencil, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'horses', name: 'Horses' },
  { id: 'tack', name: 'Tack & Saddles' },
  { id: 'equipment', name: 'Equipment' },
  { id: 'trailers', name: 'Trailers' },
  { id: 'other', name: 'Other' }
];

const CONDITIONS = [
  { id: 'new', name: 'New' },
  { id: 'like_new', name: 'Like New' },
  { id: 'good', name: 'Good' },
  { id: 'fair', name: 'Fair' }
];

const MarketplacePage = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showCreateListing, setShowCreateListing] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    category: 'equipment',
    condition: 'good',
    location: '',
    contact_method: 'both',
    contact_info: '',
    photos: []
  });

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    price: '',
    category: 'equipment',
    condition: 'good',
    location: '',
    contact_method: 'both',
    contact_info: '',
    photos: []
  });

  useEffect(() => {
    fetchListings();
  }, [filterCategory]);

  const fetchListings = async (retryCount = 0) => {
    try {
      setError(false);
      const params = filterCategory !== 'all' ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${API_URL}/api/marketplace${params}`, { timeout: 10000 });
      setListings(response.data.listings || []);
    } catch (err) {
      console.error('Failed to fetch listings:', err);
      // Retry up to 2 times on failure
      if (retryCount < 2) {
        setTimeout(() => fetchListings(retryCount + 1), 1000);
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (form.photos.length + files.length > 5) {
      alert('Maximum 5 photos allowed');
      return;
    }
    
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'marketplace');
        
        const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setForm(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.url]
        }));
      } catch (error) {
        console.error('Failed to upload photo:', error);
        alert('Failed to upload photo');
      }
    }
    
    setUploading(false);
  };

  const removePhoto = (index) => {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.price || !form.location || !form.contact_info) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/marketplace`, {
        ...form,
        price: parseFloat(form.price)
      });
      
      setShowCreateListing(false);
      setForm({
        title: '',
        description: '',
        price: '',
        category: 'equipment',
        condition: 'good',
        location: '',
        contact_method: 'both',
        contact_info: '',
        photos: []
      });
      fetchListings();
    } catch (error) {
      console.error('Failed to create listing:', error);
      alert(error.response?.data?.detail || 'Failed to create listing');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/marketplace/${listingId}`);
      setSelectedListing(null);
      fetchListings();
    } catch (error) {
      console.error('Failed to delete listing:', error);
    } finally {
      setDeleting(false);
    }
  };

  const openEditListing = (listing) => {
    setEditForm({
      title: listing.title || '',
      description: listing.description || '',
      price: listing.price?.toString() || '',
      category: listing.category || 'equipment',
      condition: listing.condition || 'good',
      location: listing.location || '',
      contact_method: listing.contact_method || 'both',
      contact_info: listing.contact_info || '',
      photos: listing.photos || []
    });
    setEditingListing(listing);
    setSelectedListing(null);
  };

  const handleEditPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (editForm.photos.length + files.length > 5) {
      alert('Maximum 5 photos allowed');
      return;
    }
    
    setUploading(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Only images are allowed');
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        alert('Image must be less than 10MB');
        continue;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'marketplace');
      
      try {
        const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setEditForm(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.url]
        }));
      } catch (error) {
        console.error('Failed to upload photo:', error);
        alert('Failed to upload photo');
      }
    }
    
    setUploading(false);
  };

  const removeEditPhoto = (index) => {
    setEditForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateListing = async (e) => {
    e.preventDefault();
    
    if (!editForm.title || !editForm.description || !editForm.price || !editForm.location || !editForm.contact_info) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/marketplace/${editingListing.id}`, {
        ...editForm,
        price: parseFloat(editForm.price)
      });
      
      setEditingListing(null);
      setEditForm({
        title: '',
        description: '',
        price: '',
        category: 'equipment',
        condition: 'good',
        location: '',
        contact_method: 'both',
        contact_info: '',
        photos: []
      });
      fetchListings();
    } catch (error) {
      console.error('Failed to update listing:', error);
      alert(error.response?.data?.detail || 'Failed to update listing');
    }
  };

  const filteredListings = listings.filter(listing =>
    listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    listing.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
              Marketplace
            </h1>
            <p className="text-muted-foreground mt-1">
              Buy and sell equipment, horses, and more within our community
            </p>
          </div>
          
          <Dialog open={showCreateListing} onOpenChange={setShowCreateListing}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-listing-btn">
                <Plus className="w-4 h-4 mr-2" />
                Post Item for Sale
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display uppercase">Create Listing</DialogTitle>
                <DialogDescription>
                  Post an item for sale to the community
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateListing} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., 15.5 inch Ranch Saddle"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">Price (CAD) *</Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="condition">Condition</Label>
                    <Select value={form.condition} onValueChange={(val) => setForm({ ...form, condition: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITIONS.map(cond => (
                          <SelectItem key={cond.id} value={cond.id}>{cond.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      placeholder="e.g., Calgary, AB"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Describe your item in detail..."
                    rows={4}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_method">Contact Preference</Label>
                    <Select value={form.contact_method} onValueChange={(val) => setForm({ ...form, contact_method: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone Only</SelectItem>
                        <SelectItem value="email">Email Only</SelectItem>
                        <SelectItem value="both">Phone & Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_info">Contact Info *</Label>
                    <Input
                      id="contact_info"
                      value={form.contact_info}
                      onChange={(e) => setForm({ ...form, contact_info: e.target.value })}
                      placeholder="Phone or email"
                      required
                    />
                  </div>
                </div>
                
                {/* Photos */}
                <div className="space-y-2">
                  <Label>Photos (up to 5)</Label>
                  <div className="flex flex-wrap gap-2">
                    {form.photos.map((photo, index) => (
                      <div key={index} className="relative w-20 h-20">
                        <img src={photo} alt="" className="w-full h-full object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {form.photos.length < 5 && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-20 h-20 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center hover:border-primary transition-colors"
                      >
                        {uploading ? (
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Image className="w-6 h-6 text-muted-foreground" />
                        )}
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateListing(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Post Listing</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search listings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-muted-foreground">Loading listings...</p>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">Unable to load listings. Please check your connection.</p>
              <Button onClick={() => { setLoading(true); fetchListings(); }} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filteredListings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No listings found. Be the first to post something!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredListings.map(listing => (
              <Card 
                key={listing.id} 
                className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow relative group"
                onClick={() => setSelectedListing(listing)}
                data-testid={`listing-${listing.id}`}
              >
                {/* Edit button - only visible to owner */}
                {user && listing.seller_id === user.id && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditListing(listing);
                    }}
                    data-testid={`edit-listing-${listing.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                <div className="aspect-square bg-muted relative">
                  {listing.photos?.length > 0 ? (
                    <img 
                      src={listing.photos[0]} 
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-muted-foreground/30" />
                    </div>
                  )}
                  <Badge className="absolute top-2 left-2 capitalize">
                    {CATEGORIES.find(c => c.id === listing.category)?.name || listing.category}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold line-clamp-1">{listing.title}</h3>
                  <p className="text-2xl font-bold text-primary mt-1">
                    {formatPrice(listing.price)}
                  </p>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                    <MapPin className="w-3 h-3" />
                    {listing.location}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Listing Detail Dialog */}
        <Dialog open={!!selectedListing} onOpenChange={(open) => !open && setSelectedListing(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedListing && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">{selectedListing.title}</DialogTitle>
                </DialogHeader>
                
                {/* Photos */}
                {selectedListing.photos?.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {selectedListing.photos.map((photo, index) => (
                      <img 
                        key={index}
                        src={photo} 
                        alt=""
                        className={`w-full object-cover rounded-lg ${index === 0 ? 'col-span-2 aspect-video' : 'aspect-square'}`}
                      />
                    ))}
                  </div>
                )}
                
                <div className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold text-primary">
                      {formatPrice(selectedListing.price)}
                    </span>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="capitalize">
                        {CONDITIONS.find(c => c.id === selectedListing.condition)?.name || selectedListing.condition}
                      </Badge>
                      <Badge className="capitalize">
                        {CATEGORIES.find(c => c.id === selectedListing.category)?.name || selectedListing.category}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {selectedListing.location}
                  </div>
                  
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap">{selectedListing.description}</p>
                  </div>
                  
                  <Card className="bg-muted/50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2">Contact Seller</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Posted by {selectedListing.seller_name}
                      </p>
                      <div className="flex items-center gap-2">
                        {(selectedListing.contact_method === 'phone' || selectedListing.contact_method === 'both') && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-4 h-4" />
                            {selectedListing.contact_info}
                          </div>
                        )}
                        {(selectedListing.contact_method === 'email' || selectedListing.contact_method === 'both') && (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="w-4 h-4" />
                            {selectedListing.contact_info}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {(selectedListing.seller_id === user?.id || user?.role === 'admin') && (
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => openEditListing(selectedListing)}
                        data-testid="edit-listing-btn"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Listing
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={() => handleDeleteListing(selectedListing.id)}
                        disabled={deleting}
                        data-testid="delete-listing-btn"
                      >
                        {deleting ? 'Deleting...' : 'Delete Listing'}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Listing Dialog */}
        <Dialog open={!!editingListing} onOpenChange={(open) => !open && setEditingListing(null)}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display uppercase">Edit Listing</DialogTitle>
              <DialogDescription>
                Update your listing details
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpdateListing} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="e.g., 15.5 inch Ranch Saddle"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-price">Price (CAD) *</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-category">Category</Label>
                  <Select 
                    value={editForm.category} 
                    onValueChange={(val) => setEditForm({ ...editForm, category: val })}
                  >
                    <SelectTrigger id="edit-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-condition">Condition</Label>
                  <Select 
                    value={editForm.condition} 
                    onValueChange={(val) => setEditForm({ ...editForm, condition: val })}
                  >
                    <SelectTrigger id="edit-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="like_new">Like New</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-location">Location *</Label>
                  <Input
                    id="edit-location"
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    placeholder="e.g., Calgary, AB"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description *</Label>
                <Textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Describe your item..."
                  rows={3}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-contact-method">Contact Method</Label>
                  <Select 
                    value={editForm.contact_method} 
                    onValueChange={(val) => setEditForm({ ...editForm, contact_method: val })}
                  >
                    <SelectTrigger id="edit-contact-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="both">Phone & Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-contact-info">Contact Info *</Label>
                  <Input
                    id="edit-contact-info"
                    value={editForm.contact_info}
                    onChange={(e) => setEditForm({ ...editForm, contact_info: e.target.value })}
                    placeholder="Phone or email"
                    required
                  />
                </div>
              </div>
              
              {/* Photos */}
              <div className="space-y-2">
                <Label>Photos ({editForm.photos.length}/5)</Label>
                <div className="grid grid-cols-5 gap-2">
                  {editForm.photos.map((photo, index) => (
                    <div key={index} className="aspect-square relative group">
                      <img 
                        src={photo} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeEditPhoto(index)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {editForm.photos.length < 5 && (
                    <label className="aspect-square border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
                      <input
                        ref={editFileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleEditPhotoUpload}
                        className="hidden"
                      />
                      {uploading ? (
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Plus className="w-6 h-6 text-muted-foreground" />
                      )}
                    </label>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditingListing(null)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MarketplacePage;
